import React from 'react';
import { Download, FileText } from 'lucide-react';
import {
  formatOmdFileSize,
  labelDocumentType,
  omdDocumentForcesDownload,
  sortByOrder,
  type OmdDocument,
  type OmdProduct,
} from '../../../utils/omdProductView';
import type { OmdDocumentsLayout, OmdPanelSettings } from '../../../utils/omdPanelSettings';
import { OmdDocumentPreview } from './OmdDocumentPreview';

const DOC_TYPE_COLORS: Record<string, string> = {
  DB: 'bg-slate-700 text-white',
  EL: 'bg-emerald-700 text-white',
  EP: 'bg-emerald-600 text-white',
  MA: 'bg-blue-600 text-white',
  BS: 'bg-indigo-600 text-white',
  IS: 'bg-indigo-600 text-white',
  CE: 'bg-amber-600 text-white',
  TZ: 'bg-violet-600 text-white',
};

type OmdDocumentCardsProps = {
  product: OmdProduct;
  settings: OmdPanelSettings;
  layout?: OmdDocumentsLayout;
  onOpenDocument?: (doc: OmdDocument) => void;
};

export function OmdDocumentCards({
  product,
  settings,
  layout,
  onOpenDocument,
}: OmdDocumentCardsProps) {
  const documents = sortByOrder((product.documents ?? []).filter((d) => d?.url));
  const viewLayout = layout ?? settings.documentsLayout;

  if (documents.length === 0) return null;

  if (viewLayout === 'liste') {
    return (
      <ul className="divide-y divide-app-border border border-app-border rounded-lg overflow-hidden">
        {documents.map((doc, idx) => (
          <li key={`${doc.url}-${idx}`}>
            <DocumentRow doc={doc} onOpen={onOpenDocument} />
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-2">
      {documents.map((doc, idx) => (
        <DocumentCard key={`${doc.url}-${idx}`} doc={doc} onOpen={onOpenDocument} />
      ))}
    </div>
  );
}

function DocumentCard({
  doc,
  onOpen,
}: {
  doc: OmdDocument;
  onOpen?: (doc: OmdDocument) => void;
}) {
  const typeClass = DOC_TYPE_COLORS[doc.type || ''] ?? 'bg-slate-600 text-white';
  const title = doc.description || doc.filename || labelDocumentType(doc.type);
  const downloadOnly = omdDocumentForcesDownload(doc);

  return (
    <a
      href={doc.url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => {
        if (downloadOnly) return;
        if (onOpen) {
          e.preventDefault();
          onOpen(doc);
        }
      }}
      className="group relative flex flex-col rounded-lg border border-app-border bg-app-surface overflow-hidden hover:border-app-border-strong transition"
    >
      <div className="aspect-[3/4] bg-app-surface-muted flex items-center justify-center overflow-hidden relative">
        <OmdDocumentPreview doc={doc} compact className="w-full h-full object-contain" />
        {doc.type && (
          <span
            className={`absolute top-2 left-2 px-1.5 py-0.5 rounded text-xs font-medium ${typeClass}`}
          >
            {doc.type}
          </span>
        )}
        <span className="absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
          <Download className="w-6 h-6 text-app-text-muted" />
        </span>
      </div>
      <div className="p-2 border-t border-app-border">
        <p className="text-xs font-medium text-app-text line-clamp-2">{title}</p>
        <p className="text-xs text-app-text-subtle mt-0.5">
          {[
            labelDocumentType(doc.type),
            formatOmdFileSize(doc.size),
            downloadOnly ? 'Download' : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
      </div>
    </a>
  );
}

function DocumentRow({ doc, onOpen }: { doc: OmdDocument; onOpen?: (doc: OmdDocument) => void }) {
  const title = doc.description || doc.filename || labelDocumentType(doc.type);
  const downloadOnly = omdDocumentForcesDownload(doc);
  return (
    <a
      href={doc.url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => {
        if (downloadOnly) return;
        if (onOpen) {
          e.preventDefault();
          onOpen(doc);
        }
      }}
      className="flex items-center gap-3 px-3 py-2.5 hover:bg-app-hover"
    >
      <FileText className="w-4 h-4 shrink-0 text-app-text-subtle" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-app-text truncate">{title}</p>
        <p className="text-xs text-app-text-subtle">
          {[labelDocumentType(doc.type), downloadOnly ? 'Download' : null].filter(Boolean).join(' · ')}
        </p>
      </div>
      {formatOmdFileSize(doc.size) && (
        <span className="text-xs text-app-text-subtle shrink-0">{formatOmdFileSize(doc.size)}</span>
      )}
    </a>
  );
}

export function OmdPictureGrid({
  product,
  onOpenLightbox,
}: {
  product: OmdProduct;
  onOpenLightbox: (index: number) => void;
}) {
  const pictures = sortByOrder((product.pictures ?? []).filter((p) => p?.url || p?.urlThumbnail));
  if (pictures.length === 0) return null;

  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
      {pictures.map((pic, idx) => (
        <button
          key={`${pic.url}-${idx}`}
          type="button"
          onClick={() => onOpenLightbox(idx)}
          className="aspect-square rounded-lg border border-app-border bg-app-surface-muted overflow-hidden hover:border-green-500/40"
        >
          <img
            src={pic.urlThumbnail || pic.url}
            alt=""
            className="w-full h-full object-contain"
            loading="lazy"
          />
        </button>
      ))}
    </div>
  );
}
